<script lang="ts">
	// Utils
	import { navigate } from "svelte-routing";
	import { clickOutsideAction } from "@hitit/hub/src/v7/utils";
	// UI
	import ComponentCover from "@hitit/hub/src/v7/components/common/ComponentCover.svelte";
	import { Card, CircleWithPlusSign, DefaultCheckbox } from "@hitit/new_ui";
	import { t } from "@hitit/hub/src/stores";
	import { storeLogos } from "@hitit/hub/src/v7/store";

	// Types
	type NameIds = {
		name: string;
		id: string;
		permission: string;
	};

	type Props = {
		cancel: () => void;
		accept: (ids: string) => void;
		storeNameIds: NameIds[];
		selectedStoreIds: string;
	};
	// Props
	export let props: Props = {
		accept: () => {},
		cancel: () => {},
		storeNameIds: [],
		selectedStoreIds: "",
	};

	//Constants
	const defaultImage = storeLogos.getDefaultImage();
	const coverColor = "rgba(70, 71, 75, 0.7)";
	const createStore = () => {
		navigate("/createstore");
		props.cancel();
	};

	const allStoreClickHandler = () => {
		const allStoreIds = props.storeNameIds.map((v) => v.id).join("|");
		props.accept(allStoreIds);
	};
	function getImage(
		storeId?: string,
		storeLogos?: { image: string; storeId: string }[]
	) {
		const find = storeLogos?.find((v) => v.storeId === storeId);
		if (!(storeLogos && storeId) || !find) {
			return defaultImage;
		}

		return find.image;
	}
	// Variables
	$: expectedAllStoreIds = props.storeNameIds.map((v) => v.id).join("|");
	$: minimumSelectAllDisplayCondition = props.storeNameIds.length >= 2;
	$: hasOwnStore =
		props.storeNameIds.filter((v) => v.permission === "STOREOWNER").length >= 1;

</script>

<ComponentCover coverColor={coverColor}>
	<div class="outer">
		<Card noPadding>
			<div class="container" use:clickOutsideAction={props.cancel}>
				<div class="title">{$t('hub.app.store_change')}</div>
				{#if minimumSelectAllDisplayCondition}
					<div class="select-all-store store" on:click={allStoreClickHandler}>
						<img src="/img/icons/allStore.png" alt="" class="store-logo" />
						<p class="name">{$t('hub.app.all_store')}</p>
						<span class="checkbox">
							<DefaultCheckbox
								checked={props.selectedStoreIds === expectedAllStoreIds} />
						</span>
					</div>
				{/if}
				<div class="stores">
					{#each props.storeNameIds as store}
						<div
							class:selected={store.id === props.selectedStoreIds}
							class="store own-store"
							on:click={() => props.accept(store.id)}>
							<img
								src={getImage(store.id, $storeLogos)}
								alt=""
								class="store-logo" />
							<p class="name">{store.name}</p>
							<span class="checkbox">
								<DefaultCheckbox
									checked={store.id === props.selectedStoreIds} />
							</span>
						</div>
					{/each}
				</div>
				{#if !hasOwnStore}
					<div class="seperate-bar" />
					<div class="add-store store" on:click={createStore}>
						<span class="icon">
							<CircleWithPlusSign />
						</span>
						<p>{$t('hub.app.add_store')}</p>
					</div>
				{/if}
			</div>
		</Card>
	</div>
</ComponentCover>

<style lang="scss">
	@import "../../../variables.scss";
	.outer {
		position: absolute;
		top: 50%;
		left: 50%;
		width: 433px;
		max-width: calc(100vw - 40px);
		transform: translate(-50%, -50%);
	}

	.container {
		width: 100%;
		padding: 20px 10px;
		display: flex;
		flex-direction: column;
		color: #46474b;
		.seperate-bar {
			width: 100%;
			height: 1px;
			background-color: #e6e7ec;
			margin: 10px 0px;
		}
		.title {
			text-align: center;
			width: 100%;
			margin-bottom: 20px;
			font-weight: 550;
		}

		.store {
			display: flex;
			align-items: center;
			.name {
				flex-grow: 1;
			}
			.checkbox {
				width: 26px;
				height: 26px;
			}
			.store-logo {
				width: 50px;
				height: 50px;
				border-radius: 50px;
				background-color: #d8d9df;
				display: inline-block;
			}
			&.selected {
				color: #3c68d6;
			}
			&:hover {
				cursor: pointer;
			}
			&.select-all-store {
				width: 100%;
				height: 80px;
				box-sizing: border-box;
				padding: 10px 21px 10px 10px;
				gap: 10px;
				background-color: #f0f0f4;
				margin-bottom: 20px;
				border-radius: 5px;
				.name {
					font-weight: 550;
				}

				.store-logo {
					width: 60px;
					height: 60px;
					border-radius: 60px;
				}
			}
			:global(svg) {
				overflow: visible;
			}
		}
		.stores {
			display: flex;
			flex-direction: column;
			gap: 10px;
		}
		.own-store {
			display: flex;
			gap: 10px;
			padding-left: 15.5px;
			padding-right: 21px;
		}

		.add-store {
			padding-left: 22.5px;
			gap: 17px;
			.icon {
				width: 35px;
				height: 35px;
			}
		}
	}

	@include DesktopSize {
		.container {
			padding: 30px;

			.seperate-bar {
				margin: 10px 0px 17px 0px;
			}
			.title {
				font-size: 1.25rem;
			}
			.store {
				&.selected {
					font-weight: 550;
				}
				&.select-all-store {
					padding: 10px 21px 10px 20px;
					gap: 20px;
				}
				font-size: 1.25rem;
			}
			.add-store {
				gap: 32.6px;
				padding-left: 32.4px;
			}
			.own-store {
				gap: 25px;
				padding-left: 25px;
			}
		}
	}

</style>
